const PAUSED_CLASS = 'is-paused';

class KBWaitlistGallery extends HTMLElement {
  #controller = new AbortController();
  /** @type {Map<HTMLVideoElement, HTMLButtonElement>} */
  #buttons = new Map();
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const { signal } = this.#controller;
    const tiles = this.querySelectorAll('.kb-waitlist-social-gallery__tile--video');
    if (tiles.length === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    tiles.forEach((tile) => {
      const video = tile.querySelector('video');
      const button = tile.querySelector('[data-kb-play]');
      if (!(video instanceof HTMLVideoElement) || !(button instanceof HTMLButtonElement)) return;

      this.#buttons.set(video, button);

      if (reduceMotion) {
        video.removeAttribute('autoplay');
        video.pause();
        this.#syncButton(video);
      }

      button.addEventListener(
        'click',
        () => {
          if (video.paused) {
            video.dataset.kbUserPaused = '';
            video.play().catch(() => {});
          } else {
            video.dataset.kbUserPaused = '1';
            video.pause();
          }
          this.#syncButton(video);
        },
        { signal }
      );

      video.addEventListener('play', () => this.#syncButton(video), { signal });
      video.addEventListener('pause', () => this.#syncButton(video), { signal });
    });

    if ('IntersectionObserver' in window && !reduceMotion) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) return;
            if (entry.isIntersecting) {
              if (video.dataset.kbUserPaused !== '1') {
                video.play().catch(() => {});
              }
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      this.#buttons.forEach((_, video) => this.#observer?.observe(video));
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#buttons.clear();
  }

  /** @param {HTMLVideoElement} video */
  #syncButton(video) {
    const button = this.#buttons.get(video);
    if (!button) return;
    const isPaused = video.paused;
    button.classList.toggle(PAUSED_CLASS, isPaused);
    button.setAttribute('aria-pressed', String(!isPaused));
    const label = isPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('kb-waitlist-gallery')) {
  customElements.define('kb-waitlist-gallery', KBWaitlistGallery);
}

export { KBWaitlistGallery };
